import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, FileText, Send, CheckCircle2, Copy } from "lucide-react";
import SellerQuestionnaire from "@/components/SellerQuestionnaire";
import VisitReportForm from "@/components/VisitReportForm";
import { copyLink } from "@/components/InviteDialog";

const STEPS = ["Fiche de visite", "Fiche entreprise", "Lien cédant"];

const Steps = ({ current, skipped }) => (
  <ol className="flex items-center gap-2 text-xs" data-testid="onsite-steps">
    {STEPS.map((s, i) => (
      <li key={s} className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold ${i < current ? "bg-[#E0C173] text-[#1B2A4A]" : i === current ? "bg-white text-[#1B2A4A]" : "bg-white/15 text-white/60"}`}>{i < current ? (i === 1 && skipped ? "–" : "✓") : i + 1}</span>
        <span className={`hidden sm:inline ${i === current ? "text-white" : "text-white/60"}`}>{s}</span>
        {i < STEPS.length - 1 && <span className="w-6 h-px bg-white/30" />}
      </li>
    ))}
  </ol>
);

const AccessStep = ({ seller, ficheDone, onFillTogether }) => {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/onsite-sellers/${seller.user_id}/send-access`);
      setResult(data);
      data.email_sent ? toast.success(`Accès envoyé à ${seller.email}`) : toast.warning("Email non envoyé : copiez le lien pour le transmettre.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="space-y-5 text-center py-6" data-testid="onsite-access">
      <CheckCircle2 className="w-14 h-14 text-[#E0C173] mx-auto" strokeWidth={1.5} />
      <h2 className="font-display text-2xl font-semibold text-[#1B2A4A]">{ficheDone ? "Fiche enregistrée" : "Compte cédant créé"}</h2>
      <p className="text-[#475569] max-w-md mx-auto">{ficheDone
        ? `La fiche de ${seller.name} est privée, en attente de votre validation. Envoyez-lui maintenant son accès pour qu'il choisisse son mot de passe et retrouve sa fiche.`
        : `Fiche de visite enregistrée et compte de ${seller.name} prêt. Envoyez-lui son lien : il configurera son compte et remplira lui-même le questionnaire sur son entreprise.`}</p>
      {!result ? (
        <button data-testid="onsite-send-access" onClick={send} disabled={busy} className="inline-flex items-center gap-2 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white px-8 py-4 rounded-full disabled:opacity-50"><Send className="w-5 h-5" /> {busy ? "Envoi…" : `Envoyer l'accès à ${seller.email}`}</button>
      ) : (
        <div className="max-w-md mx-auto space-y-3">
          <p className="text-sm text-[#1B2A4A]">{result.email_sent ? "Email envoyé. Vous pouvez aussi partager ce lien :" : `L'email n'a pas pu partir (${(result.email_error || "").replace(/\.$/, "")}). Lien à transmettre :`}</p>
          <div className="flex gap-2"><Input readOnly value={result.link} className="rounded-none text-xs" data-testid="onsite-link" /><button onClick={() => copyLink(result.link)} data-testid="onsite-copy" className="px-3 border border-gray-300"><Copy className="w-4 h-4" /></button></div>
        </div>
      )}
      {!ficheDone && <div><button data-testid="onsite-fill-together" onClick={onFillTogether} className="text-sm text-[#1B2A4A] underline">Facultatif : remplir la fiche entreprise ensemble maintenant</button></div>}
      <div className="flex flex-wrap justify-center gap-4 pt-4 text-sm">
        <Link to="/admin/rdv" reloadDocument onClick={() => sessionStorage.removeItem("proac_onsite")} data-testid="onsite-new" className="text-[#1B2A4A] border-b border-[#E0C173]">Démarrer un autre RDV</Link>
        <Link to="/admin" data-testid="onsite-back-admin" className="text-[#1B2A4A] border-b border-[#E0C173]">Retour au back-office</Link>
      </div>
    </div>
  );
};

export default function OnsiteSession() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem("proac_onsite")) || {}; } catch { return {}; } })();
  const [seller, setSeller] = useState(params.get("rdv") && saved.rdv !== params.get("rdv") ? null : saved.seller || null);
  const [qOpen, setQOpen] = useState(false);
  const [done, setDone] = useState(seller ? !!saved.done : false);
  const [ficheDone, setFicheDone] = useState(seller ? !!saved.ficheDone : false);
  useEffect(() => {
    sessionStorage.setItem("proac_onsite", JSON.stringify(seller ? { seller, done, ficheDone, rdv: params.get("rdv") || saved.rdv || "" } : {}));
  }, [seller, done, ficheDone]); // eslint-disable-line react-hooks/exhaustive-deps
  const step = done ? 2 : seller ? 1 : 0;

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <div className="bg-[#1B2A4A] px-5 sm:px-8 py-4 flex items-center justify-between gap-4 sticky top-0 z-40">
        <button onClick={() => navigate("/admin")} data-testid="onsite-exit" className="text-white/80 hover:text-white inline-flex items-center gap-1.5 text-sm"><ArrowLeft className="w-4 h-4" /> Admin</button>
        <div className="text-white font-display font-semibold">Mode RDV <span className="text-[#E0C173]">découverte</span></div>
        <Steps current={step} skipped={!ficheDone} />
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white border border-gray-200 p-6 sm:p-10">
          {step === 0 && <VisitReportForm params={params} onCreated={(s) => { setSeller(s); setDone(true); }} />}
          {step === 1 && (
            <div className="text-center space-y-5 py-6" data-testid="onsite-fiche-step">
              <FileText className="w-12 h-12 text-[#E0C173] mx-auto" strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-semibold text-[#1B2A4A]">Fiche de l'entreprise de {seller.name}</h2>
              <p className="text-[#475569]">Saisissez le SIRET pour pré-remplir la fiche, puis complétez-la ensemble. Le brouillon est enregistré sur cet appareil.</p>
              <button data-testid="onsite-open-fiche" onClick={() => setQOpen(true)} className="inline-flex items-center gap-2 bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-8 py-4 rounded-full"><FileText className="w-5 h-5" /> Ouvrir la fiche</button>
              <div><button data-testid="onsite-skip-fiche" onClick={() => setDone(true)} className="text-sm text-gray-500 underline">Passer : le cédant complétera la fiche plus tard</button></div>
            </div>
          )}
          {step === 2 && <AccessStep seller={seller} ficheDone={ficheDone} onFillTogether={() => setQOpen(true)} />}
        </div>
      </div>
      {seller && (
        <SellerQuestionnaire open={qOpen} onOpenChange={setQOpen} ownerId={seller.user_id} submitUrl={`/admin/onsite-sellers/${seller.user_id}/businesses`} successMessage={`Fiche de ${seller.name} enregistrée — privée, en attente de votre validation.`} onSubmitted={() => { setQOpen(false); setFicheDone(true); setDone(true); }} />
      )}
    </div>
  );
}
